<!--
	MenuItem: one full-width row in a dropdown or popover menu. Renders a link (`href`), an action
	button, or the current page as text (`current`); a disabled link becomes a disabled button.
-->
<script lang="ts">
	import type { Snippet } from 'svelte';

	interface Props {
		/** Callers pass resolved app routes, external URLs or static assets. */
		href?: string;
		target?: string;
		onclick?: () => void;
		disabled?: boolean;
		/** The page the user is on: shown as text with `aria-current`, not as a control. */
		current?: boolean;
		/** Trailing icon, raw SVG (CLAUDE.md SVG rule). */
		icon?: string;
		children: Snippet;
	}

	const {
		href,
		target,
		onclick,
		disabled = false,
		current = false,
		icon,
		children
	}: Props = $props();
</script>

{#snippet content()}
	<span class="label">{@render children()}</span>
	{#if icon}<span class="icon" aria-hidden="true">{@html icon}</span>{/if}
{/snippet}

{#if current}
	<span class="menuItem current" aria-current="page">{@render content()}</span>
{:else if href && !disabled}
	<!-- eslint-disable-next-line svelte/no-navigation-without-resolve -- callers pass resolved hrefs -->
	<a class="menuItem" {href} {target} rel={target === '_blank' ? 'noopener' : undefined}>
		{@render content()}
	</a>
{:else}
	<button type="button" class="menuItem" {disabled} {onclick}>{@render content()}</button>
{/if}

<style>
	/* Label column, then an icon column, so trailing icons line up down the menu. */
	.menuItem {
		display: grid;
		grid-template-columns: 1fr auto;
		align-items: center;
		gap: var(--space-4);
		transition: background-color 0.15s ease-out;
		cursor: pointer;
		border: 0;
		background: none;
		padding: var(--space-2) var(--space-3);
		width: 100%;
		min-height: var(--height-button);
		color: var(--text-color);
		font: inherit;
		font-weight: var(--weight-semibold);
		font-size: var(--font-size-sm);
		text-align: start;
		text-decoration: none;
		white-space: nowrap;

		&:hover:not(:disabled, .current) {
			background-color: var(--hover-surface);
		}

		&:active:not(:disabled, .current) {
			background-color: var(--active-surface);
		}

		&:disabled {
			cursor: not-allowed;
			color: var(--disabled-text);
		}

		/* "You are here": marked by colour and a leading bar, not interactive. */
		&.current {
			cursor: default;
			border-inline-start: 3px solid var(--accent-text);
			color: var(--accent-text);
			font-weight: var(--weight-bold);
		}

		/* Rows sit flush against each other, so the ring goes inside. */
		&:focus-visible {
			outline-offset: -2px;
		}
	}

	.icon {
		display: flex;
		flex-shrink: 0;

		:global(svg) {
			flex-shrink: 0;
			width: var(--icon-sm);
			height: var(--icon-sm);
		}
	}
</style>
